import React, { useState, useRef, useEffect } from 'react';
import { SVGIcon } from '../custom-essentials';

const LearningPlanDisplay = ({ studentId, learningPlansData, studentName }) => {
    // v2.0 - Ultra-flat design with dark popovers
    // Collapse states for completed plans
    const [expandedLPs, setExpandedLPs] = useState({});
    // Collapse states for assigned/completed sections in active plans
    const [expandedAssigned, setExpandedAssigned] = useState({});
    const [expandedCompleted, setExpandedCompleted] = useState({});
    // Click-to-show lesson list popover (better than hover for scrollable content)
    const [showLessonList, setShowLessonList] = useState({});
    const popoverRef = useRef({});

    // Close popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(popoverRef.current).forEach(lpId => {
                if (popoverRef.current[lpId] && !popoverRef.current[lpId].contains(event.target)) {
                    setShowLessonList(prev => ({ ...prev, [lpId]: false }));
                }
            });
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!learningPlansData) return null;

    const { learningPlans = [], learningPlanItems = [], lessons = [] } = learningPlansData;

    // Separate active and completed LPs
    const activeLPs = learningPlans.filter(lp => parseInt(lp.active) === 1);
    const completedLPs = learningPlans.filter(lp => parseInt(lp.active) === 0);

    // Create lesson lookup map
    const lessonMap = {};
    lessons.forEach(lesson => {
        lessonMap[lesson.id] = lesson;
    });

    // Get items for a specific learning plan
    const getItemsForLP = (lpId) => {
        return learningPlanItems.filter(item => item.learning_plan_id === lpId);
    };

    // Calculate progress for a learning plan
    const calculateProgress = (lpId) => {
        const items = getItemsForLP(lpId);
        const total = items.length;
        const completed = items.filter(item => item.date_completed !== null && item.date_completed !== undefined).length;
        return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
    };

    const toggleLP = (lpId) => {
        setExpandedLPs(prev => ({
            ...prev,
            [lpId]: !prev[lpId]
        }));
    };

    const toggleLessonList = (lpId) => {
        setShowLessonList(prev => ({
            ...prev,
            [lpId]: !prev[lpId]
        }));
    };

    return (
        <div className="space-y-2">
            {/* Active Learning Plans - Simple & Clean */}
            {activeLPs.length > 0 && activeLPs.map(lp => {
                const items = getItemsForLP(lp.id);
                const progress = calculateProgress(lp.id);
                // CORRECT LOGIC:
                // Assigned = has date_assigned (start date) but NOT completed
                const assignedItems = items.filter(item =>
                    item.date_assigned !== null &&
                    item.date_assigned !== undefined &&
                    (!item.date_completed || item.date_completed === null)
                );
                // Completed = has date_completed
                const completedItems = items.filter(item =>
                    item.date_completed !== null &&
                    item.date_completed !== undefined
                );

                // Check if sections are expanded
                const showAssigned = expandedAssigned[lp.id] || false;
                const showCompleted = expandedCompleted[lp.id] || false;

                const isPopoverOpen = showLessonList[lp.id] || false;

                return (
                    <div key={lp.id} className="border-l-4 border-blue-600 rounded shadow-md bg-white py-0.5 px-2" style={{ position: 'relative', overflow: 'visible' }}>
                        {/* Active emphasis: bold text + colored underline */}
                        <div className="flex items-center justify-between">
                            <div className="text-base font-extrabold text-blue-600 flex-1 truncate border-b-2 border-blue-600" style={{ lineHeight: '1.1', paddingBottom: '2px' }}>{lp.name}</div>
                            {/* Percentage - Click to show lesson list */}
                            <div className="relative z-10" ref={el => popoverRef.current[lp.id] = el}>
                                <button
                                    onClick={() => toggleLessonList(lp.id)}
                                    className="text-base font-bold text-blue-600 hover:text-blue-800 cursor-pointer underline decoration-dotted"
                                    title="Click to view all lessons"
                                >
                                    {progress.percentage}%
                                </button>

                                {/* Click-triggered popover - BLACK background, WHITE text, OVERLAPS frames */}
                                {/* Mobile: narrower + centered, Desktop: right-aligned */}
                                {isPopoverOpen && (
                                    <div className="absolute right-0 sm:right-0 top-full mt-1 w-[calc(100vw-2rem)] sm:w-80 max-w-sm bg-black rounded-lg shadow-2xl border-4 border-blue-400 z-[100]">
                                        <div className="px-3 py-2 border-b-2 border-blue-400 bg-gray-900">
                                            <div className="text-sm font-bold text-white">{lp.name}</div>
                                        </div>
                                        <div className="max-h-[48rem] overflow-y-auto p-3 bg-black">
                                            {items.length > 0 ? items.map(item => {
                                                const lesson = lessonMap[item.lesson_id];
                                                const isCompleted = item.date_completed !== null && item.date_completed !== undefined;
                                                return (
                                                    <div key={item.id} className="text-sm py-0.5 font-normal text-white">
                                                        <span className={`font-bold text-base mr-1 ${isCompleted ? 'text-green-400' : 'text-orange-400'}`}>
                                                            {isCompleted ? '✓' : '○'}
                                                        </span>
                                                        {lesson ? lesson.name : `Lesson ${item.lesson_id}`}
                                                    </div>
                                                );
                                            }) : (
                                                <div className="text-white text-sm py-2">No lessons found</div>
                                            )}
                                        </div>
                                        <div className="px-3 py-2 border-t-2 border-blue-400 text-sm text-white font-bold bg-gray-900">
                                            {progress.completed} of {progress.total} completed
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Progress bar below name */}
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
                            <div
                                className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${progress.percentage}%` }}
                            ></div>
                        </div>

                        {/* Lessons section - Assigned ALWAYS visible, Completed collapsible */}
                        <div className="bg-white">
                            {/* Assigned Lessons - ALWAYS VISIBLE (not dropdown) */}
                            {assignedItems.length > 0 && (
                                <div className="border-t border-gray-200 bg-orange-50">
                                    <div className="px-2 py-0.5">
                                        <div className="text-xs font-bold text-orange-700">
                                            📝 Assigned ({assignedItems.length})
                                        </div>
                                    </div>
                                    <div className="px-2 pb-1 space-y-0.5">
                                        {assignedItems.map(item => {
                                            const lesson = lessonMap[item.lesson_id];
                                            return (
                                                <div
                                                    key={item.id}
                                                    className="text-xs py-0.5 px-1 bg-white border-l-2 border-orange-500"
                                                >
                                                    {lesson ? lesson.name : `Lesson ${item.lesson_id}`}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Completed Lessons - Collapsible dropdown, VERY FLAT */}
                            {completedItems.length > 0 && (
                                <div className="border-t border-gray-200">
                                    <button
                                        onClick={() => setExpandedCompleted(prev => ({ ...prev, [lp.id]: !prev[lp.id] }))}
                                        className="w-full flex items-center justify-between px-2 py-0.5 hover:bg-green-50 active:bg-green-50 transition-colors touch-manipulation text-left"
                                    >
                                        <span className="text-xs font-bold text-green-700">
                                            {showCompleted ? '▼' : '▶'} ✅ Completed
                                        </span>
                                        <span className="bg-green-100 text-green-800 font-bold px-1.5 py-0.5 rounded text-xs">
                                            {completedItems.length}
                                        </span>
                                    </button>
                                    {showCompleted && (
                                        <div className="px-2 pb-1 space-y-0.5 bg-green-50">
                                            {completedItems.map(item => {
                                                const lesson = lessonMap[item.lesson_id];
                                                return (
                                                    <div
                                                        key={item.id}
                                                        className="text-xs py-0.5 px-1 bg-white border-l-2 border-green-500"
                                                    >
                                                        {lesson ? lesson.name : `Lesson ${item.lesson_id}`}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* No lessons message - FLAT */}
                            {assignedItems.length === 0 && completedItems.length === 0 && (
                                <div className="px-2 py-1 text-center border-t border-gray-200">
                                    <p className="text-gray-600 italic text-xs">No lessons assigned yet</p>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}

            {/* Completed Plans - ULTRA FLAT */}
            {completedLPs.length > 0 && (
                <details className="border border-gray-300 rounded-md overflow-hidden bg-gray-100">
                    <summary className="cursor-pointer px-2 py-0.5 hover:bg-gray-200 active:bg-gray-200 transition-colors touch-manipulation">
                        <span className="text-xs font-bold text-gray-700">
                            ▶ 📦 Completed Plans ({completedLPs.length})
                        </span>
                    </summary>
                    <div className="bg-white divide-y divide-gray-200">
                        {completedLPs.map(lp => {
                            const items = getItemsForLP(lp.id);
                            const progress = calculateProgress(lp.id);
                            const isExpanded = expandedLPs[lp.id] || false;

                            return (
                                <div key={lp.id}>
                                    <button
                                        onClick={() => toggleLP(lp.id)}
                                        className="w-full flex items-center justify-between px-2 py-1 hover:bg-gray-50 active:bg-gray-50 text-left text-xs touch-manipulation"
                                    >
                                        <div className="flex items-center gap-1 flex-1 min-w-0">
                                            <span className="text-gray-600 font-bold">
                                                {isExpanded ? '▼' : '▶'}
                                            </span>
                                            <div className="font-semibold text-gray-800 truncate">{lp.name}</div>
                                        </div>
                                        <div className="px-1.5 py-0.5 bg-green-100 text-green-800 font-bold rounded text-xs flex-shrink-0">
                                            {progress.percentage}%
                                        </div>
                                    </button>

                                    {isExpanded && items.length > 0 && (
                                        <div className="px-2 sm:px-3 pb-2 sm:pb-3 bg-gray-50 space-y-0.5 sm:space-y-1">
                                            {items.map(item => {
                                                const lesson = lessonMap[item.lesson_id];
                                                const isCompleted = item.date_completed !== null && item.date_completed !== undefined;
                                                return (
                                                    <div key={item.id} className="flex items-center gap-1.5 sm:gap-2 p-1 sm:p-1.5 text-xs sm:text-sm">
                                                        <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isCompleted ? 'bg-green-500' : 'bg-gray-300'
                                                            }`}>
                                                            {isCompleted && <span className="text-white text-xs sm:text-sm">✓</span>}
                                                        </div>
                                                        <div className="flex-1 text-gray-800 font-medium">
                                                            {lesson ? lesson.name : `Lesson ${item.lesson_id}`}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </details>
            )}

            {/* No Learning Plans */}
            {activeLPs.length === 0 && completedLPs.length === 0 && (
                <div className="p-3 bg-gray-50 rounded border border-gray-300 text-center">
                    <p className="text-xs text-gray-600">No learning plans assigned</p>
                </div>
            )}
        </div>
    );
};

export default LearningPlanDisplay;
